// @ts-check
import React from 'react';

import Box from '@liquidweb-dev/undercurrent/Box';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardHeader from '@liquidweb-dev/undercurrent/CardHeader';
import CpuIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cpu';
import MemoryIcon from '@liquidweb-dev/undercurrent/icons/lucide/MemoryStick';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import DisplayTypeIcon from 'components/DisplayTypeIcon';
import KeySpec from 'components/KeySpec';
import OSIcon from 'components/OSIcon';
import { stripOsName } from 'modules/queries/asset/useDetails';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

/**
 * @param {import('@liquidweb-dev/undercurrent/Card').CardProps & {
 *   cardHeaderTitle?: import('@liquidweb-dev/undercurrent/CardHeader').CardHeaderProps['title']
 *   cardHeaderProps?: import('@liquidweb-dev/undercurrent/CardHeader').CardHeaderProps
 * }} props
 */
const Wrapper = ({ cardHeaderTitle, cardHeaderProps, children, ...rest }) => {
	return (
		<Card {...rest} sx={combineSx(rest.sx || {}, { overflow: 'hidden' })}>
			{cardHeaderTitle && (
				<CardHeader bordered title={cardHeaderTitle} {...cardHeaderProps} />
			)}
			{children}
		</Card>
	);
};

/**
 * @param {import('react').ComponentProps<typeof Wrapper> & {
 *   animate?: boolean
 * }} props
 */
const Indeterminate = ({ animate, ...props }) => {
	return (
		<Wrapper {...props}>
			<CardContent component={Stack} spacing={200}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 100,
					}}
				>
					<Skeleton
						animation={animate ? 'pulse' : false}
						variant="rounded"
						sx={{ width: '112px', height: ({ size }) => size.sm }}
					/>

					<Skeleton
						animation={animate ? 'pulse' : false}
						variant="rounded"
						sx={{ width: '35px', height: ({ size }) => size.sm }}
					/>
				</Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: 100,
					}}
				>
					<KeySpec
						label="Type"
						value={
							<Skeleton
								animation={animate ? 'pulse' : false}
								variant="rounded"
								sx={{ width: '56px', height: ({ size }) => size.xS }}
							/>
						}
						minWidth={0}
					/>
					<KeySpec
						label="OS"
						value={
							<Skeleton
								animation={animate ? 'pulse' : false}
								variant="rounded"
								sx={{ width: '56px', height: ({ size }) => size.xS }}
							/>
						}
						minWidth={0}
						maxWidth="33%"
					/>

					<KeySpec
						label="CPU"
						value={
							<Skeleton
								animation={animate ? 'pulse' : false}
								variant="rounded"
								sx={{ width: '56px', height: ({ size }) => size.xS }}
							/>
						}
						minWidth={0}
					/>

					<KeySpec
						label="RAM"
						value={
							<Skeleton
								animation={animate ? 'pulse' : false}
								variant="rounded"
								sx={{ width: '56px', height: ({ size }) => size.xS }}
							/>
						}
						minWidth={0}
					/>
				</Box>
			</CardContent>
		</Wrapper>
	);
};
/**
 * @param {object} props
 * @param {import('@liquidweb-dev/undercurrent/CardHeader').CardHeaderProps} [props.cardHeaderProps]
 * @param {import('@liquidweb-dev/undercurrent/CardHeader').CardHeaderProps['title']} [props.cardHeaderTitle]
 * @param {import('@liquidweb-dev/undercurrent/Card').CardProps} [props.cardProps]
 * @param {React.ReactNode} [props.children]
 * @param {import('modules/queries/asset/useDetails').AssetDetails['cpu']} [props.cpu]
 * @param {import('modules/queries/asset/useDetails').AssetDetails['displayType']} [props.displayType]
 * @param {import('modules/queries/asset/useDetails').AssetDetails['domain']} [props.domain]
 * @param {import('modules/queries/asset/useDetails').AssetDetails['ip']} [props.ip]
 * @param {boolean} [props.isIndeterminate] similar to loading state but skeletons do not animate
 * @param {boolean} [props.isLoading] shows skeletons instead of content
 * @param {string} [props.osName]
 * @param {string} [props.osVersion]
 * @param {string} [props.billingCycle]
 * @param {boolean} [props.showCancellationSection]
 * @param {import('modules/queries/asset/useDetails').AssetDetails['ramFormatted']} [props.ramFormatted]
 */
const ServerSpecsCard = ({
	cardHeaderProps,
	cardHeaderTitle,
	cardProps,
	children,
	cpu,
	displayType,
	domain,
	ip,
	isIndeterminate,
	isLoading,
	osName,
	osVersion,
	ramFormatted,
	billingCycle,
	showCancellationSection,
}) => {
	if (isIndeterminate || isLoading) {
		return (
			<Indeterminate
				cardHeaderTitle={cardHeaderTitle}
				cardHeaderProps={cardHeaderProps}
				animate={isLoading}
				{...cardProps}
			/>
		);
	}

	const hasKeySpecs = displayType || osName || cpu || ramFormatted;
	const osNameFormatted = stripOsName(osName || '');

	return (
		<Wrapper
			cardHeaderTitle={cardHeaderTitle}
			cardHeaderProps={cardHeaderProps}
			{...cardProps}
		>
			<CardContent component={Stack} spacing={200}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						gap: 100,
					}}
				>
					<TypographyOverflow
						variant="title-xs"
						sx={{
							fontWeight: 500,
						}}
					>
						{domain}
					</TypographyOverflow>

					<Typography
						variant="body-sm"
						sx={{
							color: (theme) => theme.vars.palette.uc.text.weak,
							flexShrink: 0,
							whiteSpace: 'nowrap',
						}}
					>
						{ip}
					</Typography>
				</Box>

				{hasKeySpecs && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							gap: 100,
						}}
					>
						{billingCycle && (
							<KeySpec
								label="Billing cycle"
								value={billingCycle}
								icon={
									<DisplayTypeIcon
										displayType="billing cycle"
										sx={{ flexShrink: 0 }}
									/>
								}
								minWidth={0}
							/>
						)}
						{displayType && (
							<KeySpec
								label="Type"
								value={displayType}
								icon={
									<DisplayTypeIcon
										displayType={displayType}
										sx={{ flexShrink: 0 }}
									/>
								}
								minWidth={0}
							/>
						)}

						{osName && (
							<KeySpec
								label="OS"
								value={`${osNameFormatted} ${osVersion}`}
								icon={
									<OSIcon
										os={osName}
										sx={(theme) => ({ fontSize: theme.size.xS, flexShrink: 0 })}
									/>
								}
								minWidth={0}
								maxWidth="33%"
							/>
						)}

						{cpu && !showCancellationSection && (
							<KeySpec
								label="CPU"
								value={cpu}
								icon={<CpuIcon sx={{ flexShrink: 0 }} />}
								minWidth={0}
							/>
						)}

						{ramFormatted && !showCancellationSection && (
							<KeySpec
								label="RAM"
								value={ramFormatted}
								icon={<MemoryIcon sx={{ flexShrink: 0 }} />}
								minWidth={0}
							/>
						)}
					</Box>
				)}
			</CardContent>
			{children}
		</Wrapper>
	);
};

export default ServerSpecsCard;
